<template>
  <UiPageModal
    :fixed-close-button="false"
    wrapper-class="courses-filter-mobile"
    @click:close="$emit('click:close')"
  >
    <div class="courses-filter-mobile__wrapper">
      <header class="courses-filter-mobile__header">
        <h2 class="courses-filter-mobile__title h h--2 ui-container">
          Фильтры
        </h2>
      </header>

      <ul class="courses-filter-mobile__list">
        <li class="courses-filter-mobile__item">
          <UiCheckboxesMobileFilter
            v-model="model.companies"
            title="Партнёры"
            :list="sortedCompanies"
            show-more
            @click:more="openSearchType = FilterKeys.companies"
          />
        </li>

        <li v-if="computedFilters?.levels" class="courses-filter-mobile__item">
          <UiCheckboxesMobileFilter
            v-model="model.levels"
            title="Уровень"
            :list="computedFilters.levels"
          />
        </li>

        <li v-if="computedFilters?.duration" class="courses-filter-mobile__item">
          <UiMinMaxMobileFilter
            v-model:min="model.duration_min"
            v-model:max="model.duration_max"
            title="Длительность"
            :data="computedFilters.duration"
          />
        </li>

        <li v-if="computedFilters?.languages" class="courses-filter-mobile__item">
          <UiCheckboxesMobileFilter
            v-model="model.languages"
            title="Язык"
            :list="computedFilters.languages"
            show-more
            @click:more="openSearchType = FilterKeys.languages"
          />
        </li>
      </ul>

      <div class="courses-filter-mobile__buttons ui-shadow-10pt">
        <div class="courses-filter-mobile__buttons-container ui-container">
          <UiButton
            theme="secondary"
            size="big"
            stretch
            @click="handleClearFilter"
          >
            Сбросить всё
          </UiButton>

          <UiButton
            size="big"
            stretch
            @click="() => {
              handleSubmit()
              emit('click:close')
            }"
          >
            Применить
          </UiButton>
        </div>
      </div>
    </div>

    <UiSearchPopupFilter
      v-if="openSearchType"
      :list="listForSearchPopup"
      :active="((model[openSearchType] || []) as string[])"
      @click:submit="popupFilterSubmitHandler"
      @click:back="openSearchType = null"
    />
  </UiPageModal>
</template>

<script setup lang="ts">
  import { storeToRefs } from 'pinia'

  import UiPageModal from '@academica-box/components/Modals/UiPageModal/UiPageModal.vue'
  import UiCheckboxesMobileFilter from '@academica-box/components/Filter/mobile/UiCheckboxesMobileFilter/UiCheckboxesMobileFilter.vue'
  import UiMinMaxMobileFilter from '@academica-box/components/Filter/mobile/UiMinMaxMobileFilter/UiMinMaxMobileFilter.vue'
  import UiButton from '@academica-box/components/Buttons/UiButton/UiButton.vue'
  import UiSearchPopupFilter from '@academica-box/components/Filter/mobile/UiSearchPopupFilter/UiSearchPopupFilter.vue'

  import type { Section } from '@academica-box/components/Filter/types'

  import { useRouteQueryParams, setRouteQueryParams } from '@academica-box/composables/useRouteQueryParam'
  import { FilterKeys } from '@academica-box/types'

  import { useFiltersStore } from '@/store/filters'

  const filtersStore = useFiltersStore()
  const { computedFilters, flattenCompanies } = storeToRefs(filtersStore)

  const emit = defineEmits([
    'click:close'
  ])

  const filtersQuery = useRouteQueryParams({
    [FilterKeys.companies]: 'array',
    [FilterKeys.levels]: 'array',
    [FilterKeys.duration_min]: 'string',
    [FilterKeys.duration_max]: 'string',
    [FilterKeys.languages]: 'array',
    [FilterKeys.page]: 'string',
  })

  const openSearchType = ref<FilterKeys.companies | FilterKeys.languages | null>(null)

  const model = ref({
    companies: [...(filtersQuery.value[FilterKeys.companies] as string[] || [])],
    levels: [...(filtersQuery.value[FilterKeys.levels] as string[] || [])],
    duration_min: filtersQuery.value[FilterKeys.duration_min] as string,
    duration_max: filtersQuery.value[FilterKeys.duration_max] as string,
    languages: [...(filtersQuery.value[FilterKeys.languages] as string[] || [])],
  })

  const listForSearchPopup = computed((): Section[] => {
    if (!openSearchType.value || !computedFilters.value) {
      return []
    }

    if (openSearchType.value === FilterKeys.companies) {
      return computedFilters.value[openSearchType.value]
    }

    return [{
      title: '',
      items: computedFilters.value[openSearchType.value]
    }]
  })

  const sortedCompanies = computed(() => {
    return flattenCompanies.value.sort((a) => {
      return model.value.companies.includes(a.value) ? -1 : 0
    })
  })

  onMounted(() => {
    model.value.companies = model.value.companies.filter((company) => {
      return flattenCompanies.value.find((fCompany) => {
        return fCompany.value === company
      })
    })
  })

  watch(model.value, (newValue) => {
    if (!(newValue.duration_max || newValue.duration_min)) {
      handleSubmit()
    }
  })

  const handleSubmit = () => {
    const filtersQueryCopy = { ...filtersQuery.value }

    filtersQueryCopy[FilterKeys.companies] = model.value.companies
    filtersQueryCopy[FilterKeys.levels] = model.value.levels
    filtersQueryCopy[FilterKeys.duration_min] = model.value.duration_min
    filtersQueryCopy[FilterKeys.duration_max] = model.value.duration_max
    filtersQueryCopy[FilterKeys.languages] = model.value.languages

    setRouteQueryParams(filtersQuery, {
      ...filtersQueryCopy,
      [FilterKeys.page]: '',
    })
  }

  const popupFilterSubmitHandler = (values: string[]) => {
    if (openSearchType.value !== null) {
      model.value[openSearchType.value] = values
      openSearchType.value = null
    }
  }

  const handleClearFilter = () => {
    const res = { ...filtersQuery.value }

    res[FilterKeys.companies] = []
    res[FilterKeys.levels] = []
    res[FilterKeys.duration_min] = ''
    res[FilterKeys.duration_max] = ''
    res[FilterKeys.languages] = []

    filtersQuery.value = res

    emit('click:close')
  }
</script>

<style lang="scss">
  @use '@academica-box/utils/styles/mixins/combinations/media-reset-z' as *;

  .courses-filter-mobile {
    background-color: var(--bg-main-color);
  }

  .courses-filter-mobile__wrapper {
    background-color: var(--bg-secondary-color);
  }

  .courses-filter-mobile__header {
    margin-bottom: 6px;
    padding-top: 22px;
    padding-bottom: 22px;
    border-radius: 0 0 20px 20px;
    background-color: var(--bg-main-color);
  }

  .courses-filter-mobile__title {
    margin-bottom: 0;
  }

  .courses-filter-mobile__list {
    @include ulres;
  }

  .courses-filter-mobile__item {
    &:not(:last-child) {
      margin-bottom: 6px;
    }
  }

  .courses-filter-mobile__buttons {
    @include z-index(sticky, popup, raised);

    bottom: 0;
    padding: 20px 0;
    background-color: var(--bg-with-shadow-color);
  }

  .courses-filter-mobile__buttons-container {
    display: grid;
    grid-column-gap: 10px;
    grid-template-columns: 1fr 1fr;
  }
</style>
